import type { LiveEncoderProfile, LiveEncoderSetup } from '../types/liveEncoder';

const CUSTOM_ENCODER_PROFILES_STORAGE_KEY = 'viewsboost.live.encoderProfiles';
const DEFAULT_RTMP_BASE = (import.meta.env.VITE_LIVE_RTMP_BASE as string | undefined) || 'rtmp://your-rtmp-server/live';
const DEFAULT_RTMPS_BASE = (import.meta.env.VITE_LIVE_RTMPS_BASE as string | undefined) || '';

const PRESET_ENCODER_PROFILES: LiveEncoderProfile[] = [
  {
    id: 'preset-1080-balanced',
    name: '1080p Balanced',
    resolution: '1920x1080',
    fps: 30,
    videoBitrateKbps: 6000,
    audioBitrateKbps: 160,
    keyframeIntervalSeconds: 2,
    notes: 'Good default for public live shows that need steady quality without maxing encoder load.',
    source: 'preset',
  },
  {
    id: 'preset-1080-quality',
    name: '1080p Quality',
    resolution: '1920x1080',
    fps: 60,
    videoBitrateKbps: 9000,
    audioBitrateKbps: 192,
    keyframeIntervalSeconds: 2,
    notes: 'Best for polished studio productions with strong uplink and dedicated encoding hardware.',
    source: 'preset',
  },
  {
    id: 'preset-720-resilient',
    name: '720p Resilient',
    resolution: '1280x720',
    fps: 30,
    videoBitrateKbps: 3500,
    audioBitrateKbps: 128,
    keyframeIntervalSeconds: 2,
    notes: 'Safer profile for unstable uplinks, mobile hotspots, or backup encoders.',
    source: 'preset',
  },
];

const canUseLocalStorage = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const buildPlaybackPath = (streamKey: string) => `/live/${streamKey}`;

export const getPresetEncoderProfiles = (): LiveEncoderProfile[] => PRESET_ENCODER_PROFILES.slice();

export const getReusableEncoderProfiles = (): LiveEncoderProfile[] => {
  if (!canUseLocalStorage()) return [];
  try {
    const raw = window.localStorage.getItem(CUSTOM_ENCODER_PROFILES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as LiveEncoderProfile[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const getAvailableEncoderProfiles = (): LiveEncoderProfile[] => [
  ...getPresetEncoderProfiles(),
  ...getReusableEncoderProfiles(),
];

export const getEncoderProfileById = (profileId?: string | null): LiveEncoderProfile => {
  const match = getAvailableEncoderProfiles().find((profile) => profile.id === profileId);
  return match || PRESET_ENCODER_PROFILES[0];
};

export const saveReusableEncoderProfile = (
  profileId: string,
  customName: string,
): LiveEncoderProfile => {
  const baseProfile = getEncoderProfileById(profileId);
  const reusableProfile: LiveEncoderProfile = {
    ...baseProfile,
    id: `custom-${typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : Date.now().toString(36)}`,
    name: customName.trim(),
    source: 'custom',
  };

  if (!canUseLocalStorage()) {
    return reusableProfile;
  }

  const existing = getReusableEncoderProfiles();
  window.localStorage.setItem(
    CUSTOM_ENCODER_PROFILES_STORAGE_KEY,
    JSON.stringify([...existing, reusableProfile]),
  );
  return reusableProfile;
};

export const generateEncoderStreamKey = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 18);
  }
  return Math.random().toString(36).slice(2, 20);
};

export const buildLiveEncoderSetup = (
  profileId?: string | null,
  options?: { reservedOnly?: boolean; streamKey?: string },
): LiveEncoderSetup => {
  const profile = getEncoderProfileById(profileId);
  const streamKey = options?.streamKey || generateEncoderStreamKey();
  const ingestUrl = `${DEFAULT_RTMP_BASE.replace(/\/$/, '')}/${streamKey}`;
  const secureIngestUrl = DEFAULT_RTMPS_BASE
    ? `${DEFAULT_RTMPS_BASE.replace(/\/$/, '')}/${streamKey}`
    : null;

  return {
    ingestUrl,
    secureIngestUrl,
    streamKey,
    playbackPath: buildPlaybackPath(streamKey),
    profileId: profile.id,
    profileName: profile.name,
    providerLabel: DEFAULT_RTMPS_BASE ? 'ViewsBoost RTMP/RTMPS' : 'ViewsBoost RTMP',
    reservedOnly: options?.reservedOnly === true,
  };
};
